import React, { useState, useEffect } from 'react';
import '../styles/Login.css';

function Login({ onLogin, resetToken, onResetDone }) {
  // mode: 'login' | 'signup' | 'forgot' | 'reset'
  const [mode, setMode] = useState('login');

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');

  // Login fields
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  // Signup fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [phone, setPhone]         = useState('');
  const [boat1Name, setBoat1Name] = useState('');
  const [boat1Type, setBoat1Type] = useState('');
  const [boat2Name, setBoat2Name] = useState('');
  const [boat2Type, setBoat2Type] = useState('');

  // Reset password fields
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // If a Supabase recovery token arrives, switch to reset mode automatically
  useEffect(() => {
    if (resetToken) {
      setMode('reset');
      setError('');
      setInfo('');
    }
  }, [resetToken]);

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setInfo('');
  };

  // ── LOGIN ──────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.member));
        onLogin(data.token, data.member);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Could not connect to server. Please try again.');
    }
    setLoading(false);
  };

  // ── SIGN UP ────────────────────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!firstName || !lastName || !email || !password) {
      setError('First name, last name, email, and password are required');
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, firstName, lastName, phone, boat1Name, boat1Type, boat2Name, boat2Type })
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.member));
        onLogin(data.token, data.member);
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      setError('Could not connect to server. Please try again.');
    }
    setLoading(false);
  };

  // ── FORGOT PASSWORD ────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        setInfo('📧 Check your email — if that address is registered, a reset link has been sent.');
      } else {
        setError(data.error || 'Could not send reset email');
      }
    } catch (err) {
      setError('Could not connect to server. Please try again.');
    }
    setLoading(false);
  };

  // ── RESET PASSWORD ─────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      setLoading(false);
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: resetToken, new_password: newPassword })
      });
      const data = await response.json();
      if (data.success) {
        setInfo('✅ Password updated! You can now log in with your new password.');
        if (onResetDone) onResetDone();
        switchMode('login');
      } else {
        setError(data.error || 'Password reset failed');
      }
    } catch (err) {
      setError('Could not connect to server. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>⚓ Mooring System</h1>
          <p>Club Boat Mooring Management</p>
        </div>

        <div className="login-content">
          {error && <div className="login-error">{error}</div>}
          {info  && <div className="login-info">{info}</div>}

          {/* ── LOGIN FORM ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <p className="login-description">Log in to manage moorings.</p>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                />
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? '⏳ Logging in...' : '🔐 Log In'}
              </button>

              <p className="toggle-link">
                <button type="button" onClick={() => switchMode('forgot')} className="text-link">
                  Forgot your password?
                </button>
              </p>

              <p className="toggle-link">
                Don't have an account?{' '}
                <button type="button" onClick={() => switchMode('signup')} className="text-link">
                  Sign Up
                </button>
              </p>
            </form>
          )}

          {/* ── SIGNUP FORM ── */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup}>
              <p className="login-description">Create your account and register your boat(s).</p>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" required />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" value={lastName}  onChange={(e) => setLastName(e.target.value)}  placeholder="Last name"  required />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} />
              </div>

              <div className="form-group">
                <label>Phone (optional)</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number" />
              </div>

              <div className="form-section-label">Boat 1</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Boat Name</label>
                  <input type="text" value={boat1Name} onChange={(e) => setBoat1Name(e.target.value)} placeholder="Boat 1 name" />
                </div>
                <div className="form-group">
                  <label>Boat Type</label>
                  <input type="text" value={boat1Type} onChange={(e) => setBoat1Type(e.target.value)} placeholder="e.g., Sailboat" />
                </div>
              </div>

              <div className="form-section-label">Boat 2 (optional)</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Boat Name</label>
                  <input type="text" value={boat2Name} onChange={(e) => setBoat2Name(e.target.value)} placeholder="Boat 2 name" />
                </div>
                <div className="form-group">
                  <label>Boat Type</label>
                  <input type="text" value={boat2Type} onChange={(e) => setBoat2Type(e.target.value)} placeholder="e.g., Powerboat" />
                </div>
              </div>

              <button type="submit" className="login-btn signup-btn" disabled={loading}>
                {loading ? '⏳ Creating account...' : '✅ Create Account & Log In'}
              </button>

              <p className="toggle-link">
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} className="text-link">
                  Log In
                </button>
              </p>
            </form>
          )}

          {/* ── FORGOT PASSWORD FORM ── */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword}>
              <p className="login-description">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? '⏳ Sending...' : '📧 Send Reset Link'}
              </button>

              <p className="toggle-link">
                <button type="button" onClick={() => switchMode('login')} className="text-link">
                  ← Back to Log In
                </button>
              </p>
            </form>
          )}

          {/* ── RESET PASSWORD FORM ── */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword}>
              <p className="login-description">
                Choose a new password for your account.
              </p>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? '⏳ Updating...' : '🔐 Set New Password'}
              </button>
            </form>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <div className="login-features">
              <h3>What you can do:</h3>
              <ul>
                <li>✓ Check your boat in and out of moorings</li>
                <li>✓ Manage departure dates (1–7 days)</li>
                <li>✓ View all boats currently on moorings</li>
                <li>✓ Document unknown / poaching boats with photos</li>
                <li>✓ Coordinate with other club members</li>
              </ul>
            </div>
          )}
        </div>

        <div className="login-footer">
          <p>Version 2.1 — Mooring Management System</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
